<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useDebounceFn } from "@vueuse/core";
import { useRouteQuery } from "@vueuse/router";
import { onBeforeRouteUpdate } from "vue-router";
import CustomerSupportFilters from "@/components/customerSupport/CustomerSupportFilters.vue";
import TicketTable from "@/components/customerSupport/TicketTable.vue";
import { AppHeader } from "@/components/app";
import { Button } from "@/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import useTrackerStore from "@/stores/trackers";
import { RUDDERSTACK_EVENTS } from "@/lib/rudderstack";
import { useGetTickets } from "@/api/queries/customerSupport";
import { type TicketDashboardRequestParams, TicketStateValues } from "@telegraphio/bapi-types";

const props = defineProps<{ customerId: string }>();
const trackers = useTrackerStore();

const pageNumber = useRouteQuery("page_number", "1", { transform: Number });
const pageSize = useRouteQuery("page_size", "25", { transform: Number });
const sortColumn = useRouteQuery("sort_by", "created_ts");
const sortDirection = useRouteQuery("sort_by_direction", "ASC");

// User-controlled filters (applied via "Apply Filters" button)
const appliedFilters = ref<Partial<TicketDashboardRequestParams>>({
  priority: ["low", "medium", "high"],
  state: [TicketStateValues.UNASSIGNED, TicketStateValues.IN_PROGRESS],
});

// Merge user filters with always-reactive pagination/sorting
const queryParams = computed(() => ({
  ...appliedFilters.value,
  page: pageNumber.value,
  page_size: pageSize.value,
  sort_by: sortColumn.value,
  sort_by_direction: sortDirection.value,
}));

const customerSupportTicketsQuery = useGetTickets(props.customerId, queryParams);

const isFetching = computed(() => customerSupportTicketsQuery.isFetching.value);
const isLoading = computed(() => customerSupportTicketsQuery.isLoading.value);
const customerSupportTickets = computed(() => customerSupportTicketsQuery.data.value?.data || []);
const customerSupportReportingGroups = computed(() => customerSupportTicketsQuery.data.value?.report_groups || {});
const customerSupportTotalTickets = computed(() => customerSupportTicketsQuery.data.value?.total_record_count || 0);

const debouncedFetch = useDebounceFn(() => {
  customerSupportTicketsQuery.refetch();
}, 600);

const applyFilters = (draftFilters: Partial<TicketDashboardRequestParams>) => {
  appliedFilters.value = { ...draftFilters };
};

async function refreshTickets() {
  trackers.logRudderstackEvent(RUDDERSTACK_EVENTS.CUSTOMER_SUPPORT_TICKETS_REFRESH, {});
  customerSupportTicketsQuery.refetch();
}

watch([pageNumber, pageSize, sortColumn, sortDirection], () => {
  debouncedFetch();
});

onBeforeRouteUpdate((next, prev) => {
  if (next.name === "customerSupport" && prev.name !== "customerSupport") {
    customerSupportTicketsQuery.refetch();
  }
  if (next.name === "ticket" && (prev.name === "newTicket" || prev.name === "editTicket")) {
    customerSupportTicketsQuery.refetch();
  }
});
</script>

<template>
  <div>
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbPage>Customer Support</BreadcrumbPage>
          <BreadcrumbSeparator />
          <BreadcrumbPage>Dashboard</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
    <AppHeader>
      <template #title> Customer Support Dashboard </template>
      <template #actions>
        <router-link :to="{ name: 'newTicket', params: { customerId } }">
          <Button @click="trackers.logRudderstackEvent(RUDDERSTACK_EVENTS.CUSTOMER_SUPPORT_TICKET_START_CREATE, {})">
            <i class="fa-solid fa-plus-circle" />
            New Ticket
          </Button>
        </router-link>
      </template>
    </AppHeader>
    <div class="m-6">
      <section class="pb-0">
        <CustomerSupportFilters :default-filters="queryParams" @apply="applyFilters" />
        <TicketTable
          :customer-id="customerId"
          :tickets="customerSupportTickets"
          :reporting-groups="customerSupportReportingGroups"
          :total-tickets="customerSupportTotalTickets"
          :is-loading="isLoading"
          :is-fetching="isFetching"
          class="my-6 rounded-md border"
          @refresh="refreshTickets"
        />
        <router-view @assigned="customerSupportTicketsQuery.refetch" />
      </section>
    </div>
  </div>
</template>
